import React from "react";
import { Button } from "../../atoms/Button/Button";
import { Icomoon } from "../../atoms/Icon/Icon";
import { BulletList } from "../../molecules/BulletList/BulletList";
import { CountryImage } from "./CountryImage";

import { TrustLogo } from "../../molecules/Trust/TrustLogo";
import { TrustLink } from "../../molecules/TrustLink/TrustLink";
import "./TailorMadeTravelPillar.scss";
import { Locale } from "@travellocal/utils";
import { useAppState } from "../../utils";
import { useTranslation } from "react-i18next";

interface Props {
  lto: {
    profileImageUrl: string;
    profileImageUrlAlt: string;
    name: string;
    position: string;
    quote: string;
  };
  countryImages: Array<{
    countryImage: string;
    countryImageAlt: string;
    countryCityName: string;
    countryDayDescription: string;
  }>;
  ctaUrl: string;
  dataTrackingId: string;
  locale: Locale;
}

export const TailorMadeTravelPillar: React.FC<Props> = ({
  lto,
  countryImages,
  ctaUrl,
  dataTrackingId,
  locale,
}) => {
  const { t } = useTranslation("ui");
  const { appState } = useAppState();
  const currentLocale = locale || appState?.locale || "en-gb";

  const bulletListProps = {
    icon: Icomoon.checkmark,
    iconClassname: "has-text-teal",
    items: [
      t("TailorMadeTravelPillar_Copy1", "Tailor-made travel, made easy"),
      t("TailorMadeTravelPillar_Copy2", "24/7 in-destination support"),
      <>
        {t("TailorMadeTravelPillar_Copy3", "Peace of mind with financial protection from")}
        <TrustLink locale={currentLocale} />
      </>,
    ],
    itemClassname: "has-text-navy is-size-4 is-size-5-tablet",
  };

  return (
    <div className="tailor-made-pillar">
      <div className="text-info">
        <h3 className="auth-knowledge-title subtitle is-family-primary is-uppercase has-text-weight-semibold">
          {t("TailorMadeTravelPillar_H3", "Trips unique to you")}
        </h3>
        <h2 className="expertise-title has-text-weight-normal is-family-secondary">
          {t("TailorMadeTravelPillar_Subheader", "Tailor-made travel, made easy")}
        </h2>

        {countryImages && (
          <CountryImage className="is-hidden-tablet" lto={lto} countryImages={countryImages} />
        )}
        <div className="bullet-list-container">
          <BulletList {...bulletListProps} />

          <div className="ml-3">
            <TrustLogo
              data-tracking-id={dataTrackingId}
              locale={currentLocale}
              showTravelife={false}
            />
          </div>
        </div>
        <Button
          colour="teal"
          icomoonIcon={Icomoon.arrowRight3}
          target="_self"
          href={ctaUrl}
          data-tracking-id={dataTrackingId}
          className="cta-button">
          {t("TailorMadeTravelPillar_CTA", "Get started")}
        </Button>
      </div>
      <div className="side-wrapper">
        {countryImages && (
          <CountryImage className="is-hidden-mobile" lto={lto} countryImages={countryImages} />
        )}
      </div>
    </div>
  );
};
